import type { QuizQuestion } from '@/lib/services/questions';

export type QuizPreviewQuestionKind = 'trivia' | 'poll';

const MAX_PREVIEW_QUESTION_SLOTS = 50;
const PREVIEW_PLACEHOLDER_EDITION_ITEM_PREFIX = 'preview-placeholder-';

export function isQuizPreviewQuestionPlaceholder(
  question: Pick<QuizQuestion, 'edition_item_id' | 'question_id'> | null | undefined
): boolean {
  return (
    typeof question?.question_id === 'number' &&
    question.question_id < 0 &&
    question.edition_item_id?.startsWith(PREVIEW_PLACEHOLDER_EDITION_ITEM_PREFIX) === true
  );
}

export function serializeQuizPreviewQuestionKinds(
  kinds: readonly QuizPreviewQuestionKind[]
): string {
  return kinds.slice(0, MAX_PREVIEW_QUESTION_SLOTS).join(',');
}

export function parseQuizPreviewQuestionKinds(
  value: string | null | undefined
): QuizPreviewQuestionKind[] | null {
  if (!value) return null;
  const kinds = value.split(',');
  if (
    kinds.length === 0 ||
    kinds.length > MAX_PREVIEW_QUESTION_SLOTS ||
    kinds.some((kind) => kind !== 'trivia' && kind !== 'poll')
  ) {
    return null;
  }
  return kinds as QuizPreviewQuestionKind[];
}

function buildPlaceholderQuestion(
  kind: QuizPreviewQuestionKind,
  index: number,
  totalQuestions: number
): QuizQuestion {
  const optionLabels = ['Loading…', 'Loading…', 'Loading…', 'Loading…'];
  const stem = 'Generating your quiz…';

  return {
    question_id: -(index + 1),
    question_index: index,
    stem,
    key: kind === 'trivia' ? optionLabels[0] : '',
    distractor_1: kind === 'trivia' ? optionLabels[1] : '',
    distractor_2: kind === 'trivia' ? optionLabels[2] : '',
    distractor_3: kind === 'trivia' ? optionLabels[3] : '',
    question_fact: '',
    generation_tag_id: 0,
    breadcrumbs: [],
    edition_item_id: `${PREVIEW_PLACEHOLDER_EDITION_ITEM_PREFIX}${index + 1}`,
    item_type: kind,
    poll_definition_id: kind === 'poll' ? `preview-poll-${index + 1}` : null,
    poll_prompt: kind === 'poll' ? stem : null,
    poll_options:
      kind === 'poll'
        ? optionLabels.map((label, optionIndex) => ({
            id: `preview-poll-${index + 1}-option-${optionIndex + 1}`,
            label,
            sort_index: optionIndex,
          }))
        : null,
    is_answered: false,
    progress_status: 'not_started',
    total_questions: totalQuestions,
    answered_questions: 0,
    guess_sequence: [],
  };
}

export function reconcileQuizPreviewQuestions(
  readyQuestions: readonly QuizQuestion[],
  kinds: readonly QuizPreviewQuestionKind[]
): QuizQuestion[] {
  if (kinds.length === 0) return [...readyQuestions];

  const questionsByIndex = new Map(
    readyQuestions.map((question) => [question.question_index, question])
  );

  return kinds.map(
    (kind, index) =>
      questionsByIndex.get(index) ?? buildPlaceholderQuestion(kind, index, kinds.length)
  );
}
